import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, Chip, TextField, Typography, Divider, IconButton, useTheme, Skeleton } from "@mui/material";
import { useFormik } from "formik";
import {
	ConfirmationNumber,
	Payments,
	AccountBalanceWallet,
	TrendingUp,
	QrCode2,
	ContentCopy
} from "@mui/icons-material";
import { orange, purple, green, blue } from "@mui/material/colors";
import { LineChart } from "@mui/x-charts/LineChart";
import { format, parseISO } from "date-fns";
import ButtonComponent from "../../../Components/ButtonComponent";
import { affiliateCodeSchema } from "./schema";
import { StatWrapper } from "./styles";
import { formatCurrency } from "../../../utils/formatCurrency";
import ShareByQRModal from "../../../Components/Modals/ShareModal/ShareByQRModal";
import {
	COUPON_STATUS_LABEL,
	COUPON_STATUS_COLOR,
	CouponStatusEnum
} from "../../../store/services/affiliate/dto/affiliate.dto";
import { useAppSelector } from "../../../store";
import { getFullAffiliateState } from "../../../store/slices/affiliate/affiliate.slice";
import {
	useCreateAffiliateCouponMutation,
	useGetAffiliateSalesByMonthQuery,
	useGetUserAffiliateStatsQuery,
	useLazyGetUserAffiliateCouponQuery
} from "../../../store/services/affiliate/affiliate.service";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { handleCopyToClipboard } from "../../../utils/copyToClipboard";

const AffiliateDashboard = () => {
	const theme = useTheme();
	const {
		affiliate,
		affiliateCoupon,
		isLoadingAffiliateCoupon,
		affiliateSalesByMonth,
		isFetchingAffiliateSalesByMonth,
		affiliateStats,
		isFetchingAffiliateStats: isLoadingStats
	} = useAppSelector(getFullAffiliateState);
	useGetAffiliateSalesByMonthQuery({});
	const [FetchAffiliateCoupon] = useLazyGetUserAffiliateCouponQuery();
	const [callCreateAffiliateCoupon, { isLoading: isCreatingCoupon }] = useCreateAffiliateCouponMutation();
	useGetUserAffiliateStatsQuery({});
	const [couponStatus, setCouponStatus] = useState<CouponStatusEnum>(0);
	const [isQrModalOpen, setIsQrModalOpen] = useState(false);
	const affiliateUrl = affiliateCoupon
		? `${window.location.origin}/activateAffiliate?coupon=${affiliateCoupon.code}`
		: null;

	const monthlySales = useMemo(() => {
		return affiliateSalesByMonth.map(({ date, total }) => ({ date: parseISO(date), total }));
	}, [affiliateSalesByMonth]);

	useEffect(() => {
		if (affiliate) {
			FetchAffiliateCoupon({ affiliate_id: affiliate._id });
		}
	}, [affiliate]);

	const { values, errors, handleChange, handleSubmit, setFieldValue } = useFormik({
		initialValues: { code: "" },
		validationSchema: affiliateCodeSchema,
		onSubmit: values => {
			callCreateAffiliateCoupon({
				code: values.code.toUpperCase(),
				type: 2,
				affiliate_id: affiliate?._id || "",
				currency: "EGP"
			}).then(({ data, error }) => {
				if (data) {
					console.log(data);
				}
				if (error) {
					notifyError(error as IError);
				}
			});
		}
	});

	useEffect(() => {
		console.log(affiliateCoupon);
		if (affiliateCoupon?.code) {
			setFieldValue("code", affiliateCoupon.code);
			setCouponStatus(affiliateCoupon.status);
		}
	}, [affiliateCoupon?.code]);

	const statCards = [
		{
			label: "Code Uses",
			value: (affiliateStats?.totalUses ?? 0).toLocaleString(),
			icon: <ConfirmationNumber />,
			color: orange[500],
			sub: "Total times your code was used"
		},
		{
			label: "Sales Generated",
			value: formatCurrency(affiliateStats?.totalSales ?? 0, "EGP"),
			icon: <Payments />,
			color: purple[500],
			sub: "Total invitations sales value"
		},
		{
			label: "Net Earnings",
			value: formatCurrency(affiliateStats?.totalCommission ?? 0, "EGP"),
			icon: <AccountBalanceWallet />,
			color: green[600],
			sub: "Your commission from sales"
		},
		{
			label: "This Month",
			value: (affiliateStats?.usesThisMonth ?? 0).toLocaleString(),
			icon: <TrendingUp />,
			color: blue[500],
			sub: "Code uses this month"
		}
	];

	return (
		<Box>
			<Card sx={{ p: 3 }}>
				<Box mb={2}>
					<Typography variant={"caption"} color={"textSecondary"}>
						Business Name
					</Typography>
					<Typography variant={"subtitle1"} fontWeight={"bold"}>
						{affiliate?.businessName || "-"}
					</Typography>
				</Box>

				{isLoadingAffiliateCoupon ? (
					<Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} gap={1}>
						<Skeleton variant={"rectangular"} height={"50px"} width={"100%"} />
						<Skeleton variant={"rectangular"} height={"50px"} width={"100%"} />
					</Box>
				) : (
					<Box>
						<Box display={"flex"} gap={1} mb={2}>
							<Typography variant={"subtitle1"} fontWeight={"bold"}>
								Your Affiliate URL
							</Typography>

							{affiliateCoupon?.code && (
								<>
									<Chip label={affiliateCoupon?.code} color={"default"} size={"small"} />
									<Chip
										label={COUPON_STATUS_LABEL[couponStatus]}
										color={COUPON_STATUS_COLOR[couponStatus]}
										size={"small"}
									/>
								</>
							)}
						</Box>
						{affiliateUrl ? (
							<Box display={"flex"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
								<Box display={"flex"} justifyContent={"center"} alignItems={"center"} gap={2} flexWrap={"wrap"}>
									<Typography
										fontWeight={600}
										sx={{
											bgcolor: "action.hover",
											px: 1.5,
											py: 0.75,
											borderRadius: 1,
											wordBreak: "break-all"
										}}
									>
										{affiliateUrl}
									</Typography>
									<IconButton color={"primary"} onClick={() => handleCopyToClipboard(affiliateUrl)}>
										<ContentCopy />
									</IconButton>
									<Typography>Or</Typography>
									<IconButton color={"primary"} onClick={() => setIsQrModalOpen(true)}>
										<QrCode2 fontSize={"large"} />
									</IconButton>
								</Box>

								<Typography variant={"caption"} color={"textSecondary"} width={"100%"}>
									{couponStatus === 0
										? "This URL is awaiting admin approval and cannot be changed once created."
										: "This URL cannot be changed once created."}
								</Typography>
							</Box>
						) : (
							<form onSubmit={handleSubmit}>
								<Box display={"flex"} alignItems={"flex-start"} gap={2} flexWrap={"wrap"}>
									<TextField
										name={"code"}
										placeholder={"e.g. JANE15"}
										value={values.code}
										onChange={handleChange}
										error={!!errors.code}
										helperText={errors.code || "Max 6 characters, mix of letters and numbers"}
										inputProps={{ maxLength: 6, style: { textTransform: "uppercase" } }}
									/>
									<ButtonComponent
										variant={"contained"}
										color={"primary"}
										type={"submit"}
										text={"Create Coupon"}
										loading={isCreatingCoupon}
										disabled={isCreatingCoupon}
									/>
								</Box>
							</form>
						)}
					</Box>
				)}
			</Card>

			{isQrModalOpen && affiliateCoupon && (
				<ShareByQRModal
					id={affiliateCoupon.code}
					open={true}
					onClose={() => setIsQrModalOpen(false)}
					qr_url={affiliateCoupon.qr_image}
				/>
			)}

			<Box my={4}>
				<Divider />
			</Box>

			<Box display={"flex"} flexWrap={"wrap"} gap={2}>
				{isLoadingStats
					? [1, 2, 3, 4].map(i => <Skeleton key={i} variant={"rounded"} height={92} sx={{ flex: 1, minWidth: 185 }} />)
					: statCards.map((card, i) => (
							<StatWrapper key={i} $statColor={card.color}>
								<Box className={"icon"}>{card.icon}</Box>
								<Box className={"stats"}>
									<Typography fontWeight={800} variant={"h5"} lineHeight={1.1}>
										{card.value}
									</Typography>
									<Typography fontWeight={600} fontSize={13} mt={0.3}>
										{card.label}
									</Typography>
									<Typography color={"textSecondary"} fontSize={11} mt={0.25}>
										{card.sub}
									</Typography>
								</Box>
							</StatWrapper>
						))}
			</Box>

			<Card sx={{ p: 3, mt: 2 }}>
				<Typography fontWeight={700} fontSize={16}>
					Sales by Month
				</Typography>
				<Typography variant={"caption"} color={"textSecondary"}>
					Total invitation sales value generated by your code, per month
				</Typography>
				{isFetchingAffiliateSalesByMonth ? (
					<Skeleton variant={"rounded"} height={280} sx={{ mt: 2 }} />
				) : (
					<LineChart
						xAxis={[{ scaleType: "point", data: monthlySales.map(({ date }) => format(date, "MMM yyyy")) }]}
						series={[
							{
								data: monthlySales.map(({ total }) => total),
								label: "Sales",
								color: theme.custom_palette.color1,
								showMark: true,
								valueFormatter: value => formatCurrency(value ?? 0)
							}
						]}
						height={280}
						margin={{ left: 70, right: 20, top: 20, bottom: 30 }}
						hideLegend
					/>
				)}
			</Card>
		</Box>
	);
};

export default AffiliateDashboard;
